import type { Country } from '@/types/country';
import { getCountryImage } from '@/constants/countryImages';

export type MyTravelTab = 'lists' | 'trips';

export const MY_TRAVEL_TABS: { id: MyTravelTab; label: string }[] = [
  { id: 'lists', label: 'Lists' },
  { id: 'trips', label: 'Trips' },
];

export function parseMyTravelTab(value: string | null): MyTravelTab {
  if (value === 'trips') return value;
  return 'lists';
}

export function myTravelTabFromPath(pathname: string): MyTravelTab {
  if (pathname.startsWith('/my-travel/trips')) return 'trips';
  return 'lists';
}

export const UNKNOWN_COUNTRY_BUCKET = 'Unknown';
export const UNKNOWN_STATE_BUCKET = 'Unknown';
export const US_COUNTRY_CODE = 'US';
export const US_COUNTRY_NAME = 'United States';
export const US_STATE_GRID_ROUTE = '/saved/united-states';

const SLUG_LOWERCASE_WORDS = new Set(['and', 'of', 'the', 'de', 'du', 'da', 'la', 'le', 'el']);

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/'/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function titleCaseFromSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word, index) => {
      if (index > 0 && SLUG_LOWERCASE_WORDS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export function countryToRouteParam(country: string): string {
  return slugify(country);
}

/** Round-trip a country slug back to a canonical name. Prefer resolving against
 *  a loaded summary via `resolveCountryFromSlug`; this naive fallback is here
 *  for callers with no summary in scope. */
export function countryFromRouteParam(param: string): string {
  return titleCaseFromSlug(param);
}

export function stateToRouteParam(state: string): string {
  return slugify(state);
}

export function stateFromRouteParam(param: string): string {
  const canonical = US_STATE_NAME_BY_SLUG.get(param.toLowerCase());
  return canonical ?? titleCaseFromSlug(param);
}

/** Resolve a country slug against the loaded /saves/countries summary. Falls
 *  back to naive title-casing when the country isn't in the summary (e.g. deep
 *  link to a country the user has zero saves in). */
export function resolveCountryFromSlug(
  slug: string,
  countries: ReadonlyArray<{ country: string }> | null | undefined,
): string {
  const normalized = slug.toLowerCase();
  const match = countries?.find((c) => slugify(c.country) === normalized);
  return match?.country ?? titleCaseFromSlug(slug);
}

/** All-US-saves sentinel — used at `/saved/united-states/all` to
 *  distinguish "all US saves" from the state grid at `/united-states`. */
export const US_ALL_STATES_SLUG = 'all';

export const US_STATE_NAMES: readonly string[] = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
  'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
  'District of Columbia', 'Puerto Rico',
];

const US_STATE_NAME_BY_SLUG: ReadonlyMap<string, string> = new Map(
  US_STATE_NAMES.map((name) => [slugify(name), name]),
);

const US_STATE_CODE_TO_NAME: ReadonlyMap<string, string> = new Map([
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'], ['DC', 'District of Columbia'], ['PR', 'Puerto Rico'],
]);

/** Full state name for a US two-letter code (case-insensitive). Returns null for
 *  non-matches. Codes only — full-word inputs return null. */
export function usStateNameFromCode(code: string | null | undefined): string | null {
  if (!code) return null;
  return US_STATE_CODE_TO_NAME.get(code.trim().toUpperCase()) ?? null;
}

/** True when a saved-country tile should drill into the US-state grid instead
 *  of the flat drilldown. */
export function isUsCountryBucket(country: { country?: string; country_code?: string | null } | null | undefined): boolean {
  if (!country) return false;
  if (country.country_code && country.country_code.toUpperCase() === US_COUNTRY_CODE) return true;
  return country.country === US_COUNTRY_NAME;
}

/** Slug used for curated Unsplash fallbacks when the countries API has no match. */
export function countryNameToImageId(countryName: string): string {
  return countryName.toLowerCase().trim().replace(/\s+/g, '-');
}

/**
 * Cover image for a saved-country bucket tile — same resolution as the home
 * Destination section (backend image_url + admin override + curated fallback),
 * never saved-post sample thumbnails.
 */
export function resolveCountryCoverImage(
  countryName: string,
  countriesByName: ReadonlyMap<string, Country>,
  getCustomImage: (entityType: 'destination', entityId: string) => string | undefined,
): string {
  const country = countriesByName.get(countryName.toLowerCase());
  if (country) {
    return getCountryImage(
      country.id,
      country.image_url,
      getCustomImage('destination', country.id),
    );
  }
  return getCountryImage(countryNameToImageId(countryName));
}
